import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import { usePublicAuth } from '../context/PublicAuth';
import {
  loginEmail, registerEmail, forgotPassword, resetPassword, verifyEmail,
  resendVerification, startGoogleLogin, formatApiErrorDetail,
} from '../lib/authApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Mail, Lock, User, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';
import Seo from '../components/Seo';

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
);

const Shell = ({ children, title, subtitle }) => (
  <div className="min-h-screen bg-slate-950 text-white">
    <Seo title={`${title} | Hipro Technologies`} description="Sign in or create your Hipro Technologies account." path="/login" noindex />
    <Navbar />
    <div className="pt-28 pb-20 px-5">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest text-orange-400 font-semibold">Hipro Technologies</div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-slate-400">{subtitle}</p>}
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl p-6 sm:p-8">{children}</div>
      </div>
    </div>
    <Footer />
  </div>
);

const Field = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
    <Input {...props} className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl focus-visible:ring-orange-500" />
  </div>
);

const PasswordField = ({ value, onChange, placeholder, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input data-testid={testId} type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder} required minLength={8}
        className="pl-9 pr-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl focus-visible:ring-orange-500" />
      <button type="button" tabIndex={-1} onClick={() => setShow(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

const ErrorBox = ({ msg }) => msg ? (
  <div data-testid="auth-error" className="rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm px-3 py-2">{msg}</div>
) : null;

export function LoginPage() {
  const nav = useNavigate();
  const { user, refresh } = usePublicAuth();
  const [mode, setMode] = useState('signin'); // signin | signup | forgot | signup-done | forgot-done
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => { if (user) nav('/account', { replace: true }); }, [user, nav]);

  const run = async (fn) => {
    setBusy(true); setError(''); setNeedsVerify(false);
    try { await fn(); }
    catch (e) {
      const detail = e.response?.data?.detail;
      setError(formatApiErrorDetail(detail) || e.message);
      if (e.response?.status === 403) setNeedsVerify(true);
    }
    finally { setBusy(false); }
  };

  const onSignin = (e) => { e.preventDefault(); run(async () => { await loginEmail(email, password); await refresh(); nav('/account'); }); };
  const onSignup = (e) => { e.preventDefault(); run(async () => { await registerEmail(name, email, password); setMode('signup-done'); }); };
  const onForgot = (e) => { e.preventDefault(); run(async () => { await forgotPassword(email); setMode('forgot-done'); }); };
  const onResend = () => run(async () => { await resendVerification(email); setResent(true); });

  if (mode === 'signup-done') return (
    <Shell title="Check your inbox" subtitle={`We sent a verification link to ${email}`}>
      <div className="text-center" data-testid="signup-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
        <p className="mt-4 text-sm text-slate-300">Click the link in the email to activate your account. The link expires in 24 hours.</p>
        <Button data-testid="resend-verification-btn" onClick={onResend} disabled={busy || resent} variant="outline" className="mt-6 border-white/20 bg-transparent text-white hover:bg-white/10">
          {resent ? 'Link re-sent ✓' : 'Resend link'}
        </Button>
        <div className="mt-4"><button onClick={() => setMode('signin')} className="text-sm text-orange-400 hover:underline">Back to sign in</button></div>
      </div>
    </Shell>
  );

  if (mode === 'forgot-done') return (
    <Shell title="Check your inbox" subtitle={`If an account exists for ${email}, a reset link is on its way`}>
      <div className="text-center" data-testid="forgot-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
        <p className="mt-4 text-sm text-slate-300">The reset link expires in 1 hour.</p>
        <div className="mt-6"><button onClick={() => setMode('signin')} className="text-sm text-orange-400 hover:underline">Back to sign in</button></div>
      </div>
    </Shell>
  );

  if (mode === 'forgot') return (
    <Shell title="Forgot password" subtitle="Enter your email and we'll send you a reset link">
      <form onSubmit={onForgot} className="space-y-4" data-testid="forgot-form">
        <Field icon={Mail} data-testid="forgot-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required />
        <ErrorBox msg={error} />
        <Button data-testid="forgot-submit-btn" type="submit" disabled={busy} className="w-full h-11 bg-orange-500 hover:bg-orange-600 rounded-xl font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
        </Button>
        <div className="text-center"><button type="button" onClick={() => { setMode('signin'); setError(''); }} className="text-sm text-slate-400 hover:text-white">Back to sign in</button></div>
      </form>
    </Shell>
  );

  const isSignup = mode === 'signup';
  return (
    <Shell title={isSignup ? 'Create your account' : 'Welcome back'} subtitle={isSignup ? 'Track your job applications and get one-click apply' : 'Sign in to your Hipro account'}>
      <div className="flex rounded-xl bg-white/5 border border-white/10 p-1 mb-6">
        <button data-testid="tab-signin" onClick={() => { setMode('signin'); setError(''); }} className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${!isSignup ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}>Sign in</button>
        <button data-testid="tab-signup" onClick={() => { setMode('signup'); setError(''); }} className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${isSignup ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}>Sign up</button>
      </div>

      <form onSubmit={isSignup ? onSignup : onSignin} className="space-y-4" data-testid={isSignup ? 'signup-form' : 'signin-form'}>
        {isSignup && <Field icon={User} data-testid="signup-name-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" required />}
        <Field icon={Mail} data-testid="auth-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required />
        <PasswordField testId="auth-password-input" value={password} onChange={e => setPassword(e.target.value)} placeholder={isSignup ? 'Password (min 8 characters)' : 'Password'} />
        <ErrorBox msg={error} />
        {needsVerify && (
          <button type="button" data-testid="resend-verification-inline" onClick={onResend} disabled={busy || resent} className="text-sm text-orange-400 hover:underline">
            {resent ? 'Verification link re-sent ✓' : 'Resend verification email'}
          </button>
        )}
        {!isSignup && (
          <div className="text-right -mt-1">
            <button type="button" data-testid="forgot-password-link" onClick={() => { setMode('forgot'); setError(''); }} className="text-xs text-slate-400 hover:text-orange-400">Forgot password?</button>
          </div>
        )}
        <Button data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full h-11 bg-orange-500 hover:bg-orange-600 rounded-xl font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isSignup ? 'Create account' : 'Sign in')}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-slate-500">OR</span><div className="flex-1 h-px bg-white/10" />
      </div>
      <button data-testid="google-login-btn" onClick={() => startGoogleLogin('/account')} className="w-full h-11 flex items-center justify-center gap-2 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors">
        <GoogleIcon /> Continue with Google
      </button>
    </Shell>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = usePublicAuth();
  const token = params.get('token') || '';
  const [state, setState] = useState('working'); // working | ok | fail
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setState('fail'); setError('Missing verification token.'); return; }
    verifyEmail(token)
      .then(async () => { setState('ok'); await refresh(); setTimeout(() => nav('/account'), 1500); })
      .catch(e => { setState('fail'); setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <Shell title="Email verification">
      <div className="text-center py-4" data-testid="verify-email-status">
        {state === 'working' && (<><Loader2 className="h-10 w-10 text-orange-400 mx-auto animate-spin" /><p className="mt-4 text-sm text-slate-300">Verifying your email…</p></>)}
        {state === 'ok' && (<><CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" /><p className="mt-4 text-sm text-slate-300">Email verified! You're signed in — redirecting to your account…</p></>)}
        {state === 'fail' && (<><XCircle className="h-12 w-12 text-rose-400 mx-auto" /><p className="mt-4 text-sm text-rose-300" data-testid="verify-email-error">{error}</p>
          <Link to="/login" className="inline-block mt-6 text-sm text-orange-400 hover:underline">Go to sign in</Link></>)}
      </div>
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setBusy(true); setError('');
    try { await resetPassword(token, password); setDone(true); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  if (done) return (
    <Shell title="Password updated">
      <div className="text-center" data-testid="reset-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
        <p className="mt-4 text-sm text-slate-300">Your password has been changed. Sign in with your new password.</p>
        <Link to="/login" className="inline-block mt-6"><Button className="bg-orange-500 hover:bg-orange-600 rounded-xl font-semibold">Go to sign in</Button></Link>
      </div>
    </Shell>
  );

  return (
    <Shell title="Set a new password" subtitle="Choose a strong password (min 8 characters)">
      <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
        <PasswordField testId="reset-password-input" value={password} onChange={e => setPass(e.target.value)} placeholder="New password" />
        <PasswordField testId="reset-confirm-input" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Confirm new password" />
        <ErrorBox msg={error} />
        <Button data-testid="reset-submit-btn" type="submit" disabled={busy} className="w-full h-11 bg-orange-500 hover:bg-orange-600 rounded-xl font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update password'}
        </Button>
      </form>
    </Shell>
  );
}
